import { useRef, useState } from "react";
import { useT } from "../i18n/index.js";
import { portraitUrl } from "../assets/manifest.js";
import { Button } from "../ui/Button.js";
import { useUnits } from "./state.js";
import { unitsApi } from "./api.js";
import { moduleName, unitName } from "./shared.js";
import { ModuleArtwork, ModuleInventory } from "./ModuleInventory.js";
import { ModuleCompareSheet } from "./ModuleComparison.js";
import { EQUIPMENT_SLOTS } from "./moduleInventoryModel.js";
import type { ModuleSlot, UnitView } from "./types.js";

export function UnitEquipment({ unit, initialSlot = "weapon" }: { unit: UnitView; initialSlot?: ModuleSlot }) {
  const t = useT(), roster = useUnits(s => s.roster), run = useUnits(s => s.run);
  const [slot, setSlot] = useState<ModuleSlot>(initialSlot), [compare, setCompare] = useState<string | null>(null);
  const [busy, setBusy] = useState(false), pending = useRef(false);
  if (!roster) return null;
  const current = unit.modules.find(m => m.slot === slot);
  const remove = async () => {
    if (!current || pending.current) return;
    pending.current = true; setBusy(true);
    try { await run(() => unitsApi.unequip(unit.id, slot), t("units.toast.unequip")); }
    finally { pending.current = false; setBusy(false); }
  };
  return <div className="unit-equipment" data-testid="unit-modules">
    <header className="gear-unit-heading"><img src={portraitUrl(unit.unitId)} alt="" /><div><strong>{unitName(unit.unitId)}</strong><span>{t("gear.filled", { n: unit.modules.length })} · {t("units.compare.stat.power")} {unit.power.toLocaleString()}</span></div></header>
    <div className="gear-rack" role="group" aria-label={t("gear.slots")}>
      {EQUIPMENT_SLOTS.map(value => { const equipped = unit.modules.find(m => m.slot === value); return <button key={value} type="button" aria-pressed={slot === value} onClick={() => setSlot(value)} data-testid={`gear-slot-${value}`}>
        <ModuleArtwork slot={value} {...(equipped ? { module: equipped } : {})} size={60} /><strong>{t(`module.slot.${value}`)}</strong><small>{equipped ? `+${equipped.enhance}` : t("gear.empty")}</small>
      </button>; })}
    </div>
    <div className="gear-current" data-testid="gear-current"><div><span className="depot-kicker">{t("gear.equipped")}</span><strong>{current ? moduleName(current.moduleId) : t("gear.emptySlot")}</strong>{current ? <span>{t(`stat.${current.main.stat}`)} +{current.main.value}{current.main.mode === "pct" ? "%" : ""}</span> : null}</div>
      {current ? <Button disabled={busy} onClick={() => void remove()} data-testid={`unequip-${slot}`}>{t("units.unequip")}</Button> : null}
    </div>
    <ModuleInventory key={slot} modules={roster.modules.filter(m => m.slot === slot && m.unitRowId !== unit.id)} units={roster.units} fixedSlot={slot} onCompare={setCompare} testPrefix="equip" />
    {compare ? <ModuleCompareSheet targetRowId={unit.id} moduleId={compare} onClose={() => setCompare(null)} /> : null}
  </div>;
}
